import { useEffect, useRef, useState } from 'react'

import { evictMediaRef, resolveMediaUrl } from './robot-media-ref'

/**
 * Where a tile's picture is.
 *
 * `loading` and `failed` used to be one state: `url === null`. A tile showed the same warning icon
 * while a resolve was simply in flight as it did when one had failed, so every photo in a fresh
 * gallery flashed a warning and a genuinely failed one looked no different from a slow one.
 */
export type ResolvedMediaStatus = 'idle' | 'loading' | 'ready' | 'failed'

/** How long a failed tile waits before asking again on its own, and how many times it will. */
export const FAILED_RETRY_MS = 30_000
export const MAX_FAILED_RETRIES = 3

interface ResolveState {
  url: string | null
  status: ResolvedMediaStatus
}

/**
 * Resolve an opaque media key to a presigned URL for as long as it is on screen.
 *
 * Pass `undefined` to resolve nothing — for a row with no key, or a tile not yet scrolled into view.
 *
 * `onError` self-heals ONE lapse per successful display: a presigned URL can expire while a gallery
 * sits open, and the fix is to evict and re-resolve rather than leave a permanent broken tile. A
 * second error before any load is a dead picture and reports `failed` — it never loops.
 *
 * A failed tile retries itself a few times while it stays mounted, so a failure that outlived the
 * resolver's own back-off still recovers without a page reload.
 */
export function useResolvedMediaUrl(ref: string | undefined, robotId?: string) {
  const [state, setState] = useState<ResolveState>({ url: null, status: ref ? 'loading' : 'idle' })
  const [nonce, setNonce] = useState(0)
  const healedRef = useRef(false)
  const retriesRef = useRef(0)

  useEffect(() => {
    healedRef.current = false
    retriesRef.current = 0
  }, [ref, robotId])

  useEffect(() => {
    if (!ref) {
      setState({ url: null, status: 'idle' })

      return
    }

    setState({ url: null, status: 'loading' })

    let cancelled = false

    void resolveMediaUrl(ref, robotId).then(resolved => {
      if (!cancelled) setState({ url: resolved, status: resolved ? 'ready' : 'failed' })
    })

    return () => {
      cancelled = true
    }
  }, [ref, robotId, nonce])

  useEffect(() => {
    if (state.status !== 'failed' || !ref || retriesRef.current >= MAX_FAILED_RETRIES) return

    const timer = setTimeout(() => {
      retriesRef.current += 1
      evictMediaRef(ref, robotId)
      setNonce(n => n + 1)
    }, FAILED_RETRY_MS)

    return () => clearTimeout(timer)
  }, [state.status, ref, robotId])

  return {
    url: state.url,
    status: state.status,
    /** Call from a media element's onError. */
    handleError: () => {
      if (!ref) return

      if (healedRef.current) {
        setState({ url: null, status: 'failed' })

        return
      }

      healedRef.current = true
      evictMediaRef(ref, robotId)
      setNonce(n => n + 1)
    },
    /** Call from a media element's onLoad — re-arms one future self-heal. */
    handleLoad: () => {
      healedRef.current = false
    },
  }
}
